const decimalParts = (value, name) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
        throw new TypeError(`${name} must be a number or numeric string`);
    }

    if (!Number.isFinite(Number(value))) {
        throw new TypeError(`${name} must be finite`);
    }

    const match = String(value).trim().match(/^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i);
    if (!match || (!match[2] && !match[3])) {
        throw new TypeError(`${name} must be a valid decimal number`);
    }

    const fraction = match[3] ?? '';
    const exponent = Number(match[4] ?? 0);
    if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 308) {
        throw new RangeError(`${name} is outside the supported decimal range`);
    }

    let coefficient = BigInt(`${match[2] || '0'}${fraction}`);
    let scale = fraction.length - exponent;

    if (scale < 0) {
        coefficient *= 10n ** BigInt(-scale);
        scale = 0;
    }

    if (match[1] === '-') coefficient = -coefficient;

    return { coefficient, scale };
};

const numericValue = (value, name) => {
    decimalParts(value, name);
    const number = Number(value);
    if (!Number.isFinite(number)) {
        throw new TypeError(`${name} must be finite`);
    }
    return number;
};

const sumPercents = (values) => (
    Number(values.reduce((sum, value) => sum + value, 0).toFixed(12))
);

const sumAmounts = (values) => values.reduce((sum, value) => sum + value, 0);

const getLevels = (model) => {
    if (!model || !Array.isArray(model.levels)) {
        throw new TypeError('model.levels must be an array');
    }

    return model.levels.map((level, index) => ({
        level: index + 1,
        percent: numericValue(level?.percent, `model.levels[${index}].percent`),
    }));
};

const assertNodeIndex = (nodeIndex, depth) => {
    if (!Number.isInteger(nodeIndex) || nodeIndex < 0 || nodeIndex > depth) {
        throw new RangeError(`nodeIndex must be between 0 and ${depth}`);
    }
};

// Whole rials, rounded half up (matches the real ledger).
export const calculatePayoutAmount = (salesAmount, percent) => {
    const sales = decimalParts(salesAmount, 'salesAmount');
    const rate = decimalParts(percent, 'percent');
    const numerator = sales.coefficient * rate.coefficient;
    const denominator = 10n ** BigInt(sales.scale + rate.scale + 2);
    const isNegative = numerator < 0n;
    const absoluteNumerator = isNegative ? -numerator : numerator;
    const rounded = (absoluteNumerator + denominator / 2n) / denominator;

    return Number(isNegative ? -rounded : rounded);
};

/** What happens when ONE node buys. */
export const computeBuyerPayouts = (
    model,
    salesAmount,
    marginPercent,
    minMargin,
    buyerIndex,
) => {
    const levels = getLevels(model);
    assertNodeIndex(buyerIndex, levels.length);
    const margin = numericValue(marginPercent, 'marginPercent');
    const minimumMargin = numericValue(minMargin, 'minMargin');
    const payouts = levels.map(({ level, percent }) => ({
        level,
        percent,
        amount: calculatePayoutAmount(salesAmount, percent),
    }));
    const paidLevels = payouts.filter(({ level }) => level <= buyerIndex);
    const unpaidLevels = payouts.filter(({ level }) => level > buyerIndex);
    const totalCommissionPercent = sumPercents(
        paidLevels.map(({ percent }) => percent),
    );
    const unpaidPercent = sumPercents(
        unpaidLevels.map(({ percent }) => percent),
    );
    const remainingProfitPercent = Number(
        (margin - totalCommissionPercent).toFixed(12),
    );

    return {
        buyerIndex,
        paidUplines: paidLevels.map((payout) => ({
            ...payout,
            nodeIndex: buyerIndex - payout.level,
        })),
        unpaidLevels,
        totalCommissionPercent,
        totalCommissionAmount: calculatePayoutAmount(
            salesAmount,
            totalCommissionPercent,
        ),
        unpaidPercent,
        platformKeptAmount: sumAmounts(unpaidLevels.map(({ amount }) => amount)),
        remainingProfitPercent,
        remainingProfitAmount: calculatePayoutAmount(
            salesAmount,
            remainingProfitPercent,
        ),
        isSafe: remainingProfitPercent >= minimumMargin,
    };
};

/** What ONE node earns when each of its downline nodes buys once. */
export const computeNodeEarnings = (model, salesAmount, nodeIndex) => {
    const levels = getLevels(model);
    assertNodeIndex(nodeIndex, levels.length);

    const rows = levels.flatMap(({ level, percent }) => {
        const buyerIndex = nodeIndex + level;
        if (buyerIndex > levels.length) return [];

        return [{
            buyerIndex,
            level,
            percent,
            amount: calculatePayoutAmount(salesAmount, percent),
        }];
    });

    return {
        nodeIndex,
        rows,
        totalPercent: sumPercents(rows.map(({ percent }) => percent)),
        totalAmount: sumAmounts(rows.map(({ amount }) => amount)),
    };
};

/**
 * SEVERAL buyers each buy once. For every node we return the stack of
 * "boxes" it receives (one per paying buyer), ordered nearest buyer first.
 * Built on computeBuyerPayouts so rounding is identical.
 *
 * purchase.totalAmount is the sum of the per-level rounded amounts, so the
 * matrix always adds up. It can differ by 1 rial from
 * purchase.totalCommissionAmount (rounded once from the total percent).
 */
export const computeMultiBuyerPayouts = (
    model,
    salesAmount,
    marginPercent,
    minMargin,
    buyerIndices,
) => {
    const depth = getLevels(model).length;
    const buyers = [...new Set(buyerIndices)].sort((a, b) => a - b);
    buyers.forEach((index) => assertNodeIndex(index, depth));

    const purchases = buyers.map((buyerIndex) => {
        const single = computeBuyerPayouts(
            model,
            salesAmount,
            marginPercent,
            minMargin,
            buyerIndex,
        );
        const payouts = single.paidUplines.map(
            ({ nodeIndex, level, percent, amount }) => ({
                nodeIndex,
                level,
                percent,
                amount,
            }),
        );

        return {
            buyerIndex,
            label: String.fromCharCode(65 + buyerIndex),
            payouts,
            totalPercent: single.totalCommissionPercent,
            totalAmount: sumAmounts(payouts.map(({ amount }) => amount)),
            totalCommissionAmount: single.totalCommissionAmount,
            unpaidPercent: single.unpaidPercent,
            platformKeptAmount: single.platformKeptAmount,
            remainingProfitPercent: single.remainingProfitPercent,
            remainingProfitAmount: single.remainingProfitAmount,
            isSafe: single.isSafe,
        };
    });

    const perNode = Array.from({ length: depth + 1 }, (_, nodeIndex) => {
        const boxes = purchases
            .flatMap((purchase) => purchase.payouts
                .filter((payout) => payout.nodeIndex === nodeIndex)
                .map(({ level, percent, amount }) => ({
                    buyerIndex: purchase.buyerIndex,
                    level,
                    percent,
                    amount,
                })))
            .sort((a, b) => a.level - b.level || a.buyerIndex - b.buyerIndex);

        return {
            nodeIndex,
            boxes,
            total: sumAmounts(boxes.map(({ amount }) => amount)),
        };
    });

    return {
        buyerIndices: buyers,
        purchases,
        perNode,
        totalCommissionAmount: sumAmounts(purchases.map((p) => p.totalAmount)),
        totalCommissionPercent: sumPercents(purchases.map((p) => p.totalPercent)),
        platformKeptAmount: sumAmounts(purchases.map((p) => p.platformKeptAmount)),
        remainingProfitAmount: sumAmounts(
            purchases.map((p) => p.remainingProfitAmount),
        ),
        isSafe: purchases.every((purchase) => purchase.isSafe),
    };
};